import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import './HandDiagram.css';

function HandDiagram({ hand, boardNumber, showCopyPBN, onCopyPBN, showDDA }) {
  const diagramRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  async function generateImage() {
    if (!diagramRef.current) return;
    setGenerating(true);
    let offscreenContainer = null;
    try {
      // html2canvas miscalculates coordinates for content nested inside a
      // position:fixed ancestor (e.g. the mobile preview popup), causing
      // cropping/whitespace on mobile. Sidestep it entirely by capturing an
      // isolated clone attached directly to <body>, outside that ancestry.
      const clone = diagramRef.current.cloneNode(true);
      clone.classList.add('hand-diagram-print');

      offscreenContainer = document.createElement('div');
      offscreenContainer.style.position = 'fixed';
      offscreenContainer.style.top = '0';
      offscreenContainer.style.left = '-10000px';
      offscreenContainer.style.width = 'max-content';
      offscreenContainer.appendChild(clone);
      document.body.appendChild(offscreenContainer);

      // Wait for the browser to lay out and apply styles before capturing
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const targetWidth = clone.scrollWidth;
      const targetHeight = clone.scrollHeight;
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: targetWidth,
        height: targetHeight,
        windowWidth: targetWidth,
        windowHeight: targetHeight,
      });

      // Make the output a square by padding the shorter dimension
      const size = Math.max(canvas.width, canvas.height);
      const square = document.createElement('canvas');
      square.width = size;
      square.height = size;
      const ctx = square.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(
        canvas,
        Math.round((size - canvas.width) / 2),
        Math.round((size - canvas.height) / 2)
      );

      // Convert to pure black and white (no grey tones) via thresholded luma
      const imageData = ctx.getImageData(0, 0, size, size);
      const pixels = imageData.data;
      for (let i = 0; i < pixels.length; i += 4) {
        const grey = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
        const bw = grey > 128 ? 255 : 0;
        pixels[i] = pixels[i + 1] = pixels[i + 2] = bw;
      }
      ctx.putImageData(imageData, 0, 0);

      const link = document.createElement('a');
      link.download = `Board ${boardNumber || 'hand'}.png`;
      link.href = square.toDataURL('image/png');
      link.click();
    } finally {
      offscreenContainer?.remove();
      setGenerating(false);
    }
  }
  if (!hand || !hand.deal) {
    return <div className="hand-diagram">No hand record available</div>;
  }

  const { deal, dealer, vulnerable, ddData } = hand;
  
  function formatSuit(cards) {
    if (!cards) return '-';

    const rankOrder = { 'A': 14, 'K': 13, 'Q': 12, 'J': 11, 'T': 10, '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2 };
    const sortedCards = cards.split('').sort((a, b) => {
      return (rankOrder[b] || 0) - (rankOrder[a] || 0);
    });

    return sortedCards.join('');
  }

  function getSuitSymbol(suit) {
    const symbols = {
      spades: '♠',
      hearts: '♥',
      diamonds: '♦',
      clubs: '♣'
    };
    return symbols[suit];
  }

  function getSuitClass(suit) {
    return suit === 'hearts' || suit === 'diamonds' ? 'red' : 'black';
  }

  function isVulnerable(position) {
    if (vulnerable === 'None') return false;
    if (vulnerable === 'All') return true;
    if (vulnerable === 'NS' && (position === 'N' || position === 'S')) return true;
    if (vulnerable === 'EW' && (position === 'E' || position === 'W')) return true;
    return false;
  }

  function Hand({ position, cards }) {
    return (
      <div className={`hand hand-${position.toLowerCase()}`}>
        <div className="hand-cards">
          {['spades', 'hearts', 'diamonds', 'clubs'].map(suit => (
            <div key={suit} className="suit-line">
              <span className={`suit-symbol ${getSuitClass(suit)}`}>
                {getSuitSymbol(suit)}
              </span>
              <span className="cards">{formatSuit(cards[suit])}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="hand-diagram" ref={diagramRef}>
      <div className="hand-grid-3x3">
        <div className="grid-cell top-left-cell">
          {boardNumber && (
            <div className="board-number-label">
              Board {boardNumber}
            </div>
          )}
        </div>
        <div className="grid-cell north-cell">
          <Hand position="N" cards={deal.N} />
        </div>
        <div className="grid-cell top-right-cell">
          {showCopyPBN && onCopyPBN && (
            <div className="hand-action-buttons">
              <button className="copy-pbn-btn-grid" onClick={onCopyPBN}>
                Copy PBN
              </button>
              <button
                className="copy-pbn-btn-grid generate-image-btn"
                onClick={generateImage}
                disabled={generating}
              >
                {generating ? 'Generating…' : 'Generate Image'}
              </button>
            </div>
          )}
        </div>
        
        <div className="grid-cell west-cell">
          <Hand position="W" cards={deal.W} />
        </div>
        <div className="grid-cell center-cell">
          <div className="compass-container">
            <div className={`compass-position compass-north ${isVulnerable('N') ? 'vulnerable' : 'not-vulnerable'} ${dealer === 'N' ? 'dealer' : ''}`}>
              N
            </div>
            <div className="compass-middle-row">
              <div className={`compass-position compass-west ${isVulnerable('W') ? 'vulnerable' : 'not-vulnerable'} ${dealer === 'W' ? 'dealer' : ''}`}>
                W
              </div>
              <div className="board-number">
                {boardNumber || ''}
              </div>
              <div className={`compass-position compass-east ${isVulnerable('E') ? 'vulnerable' : 'not-vulnerable'} ${dealer === 'E' ? 'dealer' : ''}`}>
                E
              </div>
            </div>
            <div className={`compass-position compass-south ${isVulnerable('S') ? 'vulnerable' : 'not-vulnerable'} ${dealer === 'S' ? 'dealer' : ''}`}>
              S
            </div>
          </div>
        </div>
        <div className="grid-cell east-cell">
          <Hand position="E" cards={deal.E} />
        </div>
        
        <div className="grid-cell empty-cell"></div>
        <div className="grid-cell south-cell">
          <Hand position="S" cards={deal.S} />
        </div>
        <div className="grid-cell bottom-right-cell">
          {showDDA && ddData && (
            <div className="dda-compact">
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>NT</th>
                    <th>♠</th>
                    <th>♥</th>
                    <th>♦</th>
                    <th>♣</th>
                  </tr>
                </thead>
                <tbody>
                  {['N', 'S', 'E', 'W'].map(declarer => (
                    <tr key={declarer}>
                      <td>{declarer}</td>
                      <td>{ddData[declarer]?.NT || '-'}</td>
                      <td>{ddData[declarer]?.S || '-'}</td>
                      <td>{ddData[declarer]?.H || '-'}</td>
                      <td>{ddData[declarer]?.D || '-'}</td>
                      <td>{ddData[declarer]?.C || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HandDiagram;
